import type { Translate } from '@client-app/i18n';

type StaticCapabilityMode = 'read' | 'write';

type StaticCapabilityKeyCatalog = Record<StaticCapabilityMode, string[]>;

export interface StaticIntegrationCapabilityCopy {
    read: string[];
    write: string[];
}

const normalizeToolKey = (tool: string) => (
    tool
        .trim()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
);

const COMMERCE_READ_KEYS = [
    'storeInformationAndSettings',
    'productCatalogAndVariants',
    'ordersAndOrderDetails',
    'customerProfiles',
    'inventoryLevels',
];

const COMMERCE_WRITE_KEYS = [
    'createUpdateOrDeleteProducts',
    'adjustInventoryLevels',
];

const STATIC_CAPABILITY_KEYS: Record<string, StaticCapabilityKeyCatalog> = {
    shopify: {
        read: [
            'storeInformationAndSettings',
            'productCatalogAndVariants',
            'ordersAndOrderDetails',
            'customerProfilesAndSegments',
            'inventoryLevels',
            'discountCodes',
            'storeAnalyticsAndPerformanceData',
        ],
        write: [
            'createUpdateOrDeleteProducts',
            'uploadProductImages',
            'publishProductsToSalesChannels',
            'updateProductVariants',
            'adjustInventoryLevels',
            'updateOrderDetails',
            'editOrderLineItems',
            'updateCustomerInformation',
            'createUpdateOrDeleteDiscounts',
        ],
    },
    bigcommerce: {
        read: COMMERCE_READ_KEYS,
        write: COMMERCE_WRITE_KEYS,
    },
    woocommerce: {
        read: COMMERCE_READ_KEYS,
        write: [
            'createUpdateOrDeleteProducts',
            'updateOrderDetails',
            'updateCustomerInformation',
            'adjustInventoryLevels',
        ],
    },
    magento: {
        read: [
            'storeInformationAndSettings',
            'catalogProductsAndVariants',
            'ordersAndOrderDetails',
            'customerProfiles',
            'inventoryLevels',
        ],
        write: [
            'createUpdateOrDeleteCatalogProducts',
            'adjustInventoryLevels',
        ],
    },
    prestashop: {
        read: [
            'storeInformationAndSettings',
            'productCatalogAndVariants',
            'ordersAndOrderDetails',
            'customerProfiles',
            'inventoryLevels',
            'discountCodes',
        ],
        write: [
            'createUpdateOrDeleteProducts',
            'updateProductVariants',
            'adjustInventoryLevels',
            'updateOrderDetails',
            'updateCustomerInformation',
            'createUpdateOrDeleteDiscounts',
        ],
    },
    wix: {
        read: [
            'storeInformationAndSettings',
            'productCatalogAndVariants',
            'ordersAndOrderDetails',
            'customerProfiles',
            'inventoryLevels',
            'collections',
        ],
        write: [
            'createUpdateOrDeleteProducts',
            'updateOrderDetails',
            'updateCustomerInformation',
            'adjustInventoryLevels',
            'updateCollections',
        ],
    },
    aliexpress: {
        read: [
            'productSearchResults',
            'productDetails',
            'pricingAndAvailability',
            'productImages',
            'skuVariants',
        ],
        write: [],
    },
    meta_ads: {
        read: [
            'adAccounts',
            'campaigns',
            'adSets',
            'ads',
            'adCreatives',
            'customAudiences',
            'targeting',
            'analytics',
            'comments',
            'conversionDestinations',
        ],
        write: [
            'updateCampaigns',
            'updateAdSets',
        ],
    },
    google_ads: {
        read: [
            'customerAccounts',
            'campaigns',
            'adGroups',
            'ads',
            'assets',
            'keywords',
            'reports',
        ],
        write: [
            'createOrUpdateCampaigns',
            'createOrUpdateAdGroups',
            'createOrUpdateAds',
            'createAndAttachAssets',
            'createOrUpdateKeywords',
        ],
    },
    tiktok_ads: {
        read: [
            'advertisers',
            'campaigns',
            'adGroups',
            'ads',
            'reports',
        ],
        write: [
            'updateCampaigns',
            'updateAdGroups',
        ],
    },
    pinterest_ads: {
        read: [
            'adAccounts',
            'campaigns',
            'adGroups',
            'promotedPins',
            'audiences',
            'targeting',
            'analytics',
        ],
        write: [
            'updateCampaigns',
            'updateAdGroups',
        ],
    },
    x_ads: {
        read: [
            'adAccounts',
            'campaigns',
            'lineItems',
            'promotedTweets',
            'targeting',
            'analytics',
        ],
        write: [
            'updateCampaigns',
            'updateLineItems',
            'createOrPromoteTweets',
        ],
    },
    facebook_page: {
        read: [
            'pages',
            'posts',
            'comments',
            'pageInsights',
            'messengerConversations',
            'messengerPersistentMenu',
        ],
        write: [
            'createPagePostsDraftsAndScheduledPosts',
            'replyToComments',
        ],
    },
    instagram: {
        read: [
            'account',
            'media',
            'comments',
            'insights',
            'audience',
            'messages',
            'iceBreakers',
        ],
        write: [
            'createMediaPostsDraftsAndScheduledPosts',
            'replyToComments',
        ],
    },
    klaviyo: {
        read: [
            'account',
            'profiles',
            'lists',
            'segments',
            'campaigns',
            'flows',
            'catalogItems',
            'templates',
            'metrics',
            'events',
            'reports',
        ],
        write: [
            'manageProfiles',
            'manageLists',
            'manageSegments',
            'manageCampaigns',
            'manageFlows',
            'manageTemplates',
            'updateMetrics',
            'manageEvents',
        ],
    },
    mailchimp: {
        read: [
            'account',
            'audiences',
            'audienceMembers',
            'segments',
            'campaigns',
            'templates',
            'reports',
        ],
        write: [
            'createAndUpdateAudiences',
            'addUpdateUnsubscribeAndTagAudienceMembers',
            'manageSegments',
            'manageCampaigns',
            'manageTemplates',
        ],
    },
    omnisend: {
        read: [
            'contacts',
            'campaigns',
            'ordersAndOrderDetails',
            'productCatalogAndVariants',
            'events',
        ],
        write: [
            'manageContacts',
            'manageOrders',
            'createUpdateOrDeleteProducts',
            'manageEvents',
        ],
    },
    insider_one: {
        read: [
            'contacts',
            'campaigns',
            'events',
            'analytics',
        ],
        write: [
            'manageContacts',
            'manageCampaigns',
            'manageEvents',
        ],
    },
    emarsys: {
        read: [
            'contacts',
            'campaigns',
            'messages',
            'analytics',
        ],
        write: [
            'manageContacts',
            'manageCampaigns',
        ],
    },
    google_analytics: {
        read: [
            'accounts',
            'properties',
            'audiences',
            'customDimensions',
            'reports',
        ],
        write: [
            'createAndUpdateAudiences',
            'createAndUpdateCustomDimensions',
        ],
    },
    google_search_console: {
        read: [
            'sites',
            'searchAnalytics',
            'inspectUrls',
            'sitemaps',
        ],
        write: [
            'addOrRemoveSites',
            'submitOrRemoveSitemaps',
            'requestIndexingUpdates',
        ],
    },
    semrush: {
        read: [
            'domainAnalytics',
            'keywordRankings',
            'backlinkInsights',
            'competitorResearch',
        ],
        write: [],
    },
    similarweb: {
        read: [
            'websiteTraffic',
            'marketIntelligence',
            'competitorAnalytics',
        ],
        write: [],
    },
    ahrefs: {
        read: [
            'seoUsage',
            'backlinkInsights',
            'domainRating',
        ],
        write: [],
    },
    okendo: {
        read: [
            'reviews',
            'reviewSummaries',
            'customerFeedbackProfiles',
            'surveyResponses',
            'quizResponses',
            'webhookSubscriptions',
        ],
        write: [
            'manageReviews',
            'createReviewGroups',
        ],
    },
    judgeme: {
        read: [
            'productCatalogAndVariants',
            'reviews',
            'reviewSummaries',
        ],
        write: [
            'manageReviews',
            'sendReviewRequests',
        ],
    },
    judge_me: {
        read: [
            'productCatalogAndVariants',
            'reviews',
            'reviewSummaries',
        ],
        write: [
            'manageReviews',
            'sendReviewRequests',
        ],
    },
    feishu: {
        read: [
            'feishuAuthorizedUserAndContactProfiles',
            'feishuCalendars',
            'feishuDocs',
            'feishuSpreadsheets',
            'feishuBitable',
            'feishuTasks',
        ],
        write: [
            'feishuManageCalendarAttendees',
            'feishuManageDocsAndWiki',
            'feishuWriteSpreadsheets',
            'feishuManageBitableRecords',
            'feishuManageTasks',
        ],
    },
};

const translateCapabilityItem = (t: Translate, itemKey: string) => {
    const translationKey = `website.integrationCapabilityItems.${itemKey}`;
    const translated = t(translationKey);
    return translated === translationKey ? itemKey : translated;
};

export const getStaticIntegrationCapabilityCopy = (
    tool: string,
    t: Translate,
): StaticIntegrationCapabilityCopy | null => {
    const catalog = STATIC_CAPABILITY_KEYS[normalizeToolKey(tool)];
    if (!catalog) {
        return null;
    }

    return {
        read: catalog.read.map(itemKey => translateCapabilityItem(t, itemKey)),
        write: catalog.write.map(itemKey => translateCapabilityItem(t, itemKey)),
    };
};

export const getStaticIntegrationCapabilityToolKeys = () => (
    Object.keys(STATIC_CAPABILITY_KEYS)
);
